import { useMemo, useState, useCallback } from 'react';
import { useLocations } from '@/Locations/hooks/use-locations';
import { useAds } from '@/Ads/hooks/use-ads';
import { useRecipes } from '@/Recipes/hooks/use-recipes';
import { useCoupons } from '@/Coupons/hooks/use-coupons';
import { Location } from '@/Locations/types/locations';
import { AdWithLocation } from '@/Ads/hooks/use-ads';
import { Recipe } from '@/Recipes/types/recipes';
import { Coupon } from '@/Coupons/types/coupons';

export interface SearchResultLocation {
  type: 'location';
  title: string;
  subtitle: string;
  data: Location;
}

export interface SearchResultAd {
  type: 'ad';
  title: string;
  subtitle: string;
  data: AdWithLocation;
}

export interface SearchResultRecipe {
  type: 'recipe';
  title: string;
  subtitle: string;
  data: Recipe;
}

export interface SearchResultCoupon {
  type: 'coupon';
  title: string;
  subtitle: string;
  data: Coupon;
}

export type SearchResult = SearchResultLocation | SearchResultAd | SearchResultRecipe | SearchResultCoupon;

export function useSearch() {
  const [query, setQuery] = useState('');
  const { data: locations } = useLocations();
  const { adsWithLocations } = useAds();
  const { data: recipes } = useRecipes();
  const { data: coupons } = useCoupons();

  const results = useMemo<SearchResult[]>(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed || trimmed.length < 2) return [];

    const searchResults: SearchResult[] = [];

    // Search locations
    if (locations) {
      locations.forEach((location) => {
        const searchable = [
          location.LocationTitle,
          location.LocationAddress.LocationAddressCity,
          location.LocationAddress.LocationAddressState,
          location.LocationAddress.LocationAddressLineOne,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (searchable.includes(trimmed)) {
          searchResults.push({
            type: 'location',
            title: location.LocationTitle,
            subtitle: `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState}`,
            data: location,
          });
        }
      });
    }

    // Search ads
    if (adsWithLocations) {
      adsWithLocations.forEach((adWithLoc) => {
        const searchable = [
          adWithLoc.ad.AdTitle,
          adWithLoc.ad.ad_type?.AdType,
          adWithLoc.location.title,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (searchable.includes(trimmed)) {
          searchResults.push({
            type: 'ad',
            title: adWithLoc.ad.AdTitle,
            subtitle: `${adWithLoc.location.title} • ${adWithLoc.ad.ad_type?.AdType || 'Ad'}`,
            data: adWithLoc,
          });
        }
      });
    }

    // Search recipes from API
    if (recipes) {
      recipes.forEach((recipe) => {
        const searchable = [
          recipe.RecipeTitle,
          recipe.RecipeDescription,
          recipe.recipe_cuisine?.RecipeCuisine,
          recipe.recipe_type?.RecipeType,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (searchable.includes(trimmed)) {
          searchResults.push({
            type: 'recipe',
            title: recipe.RecipeTitle,
            subtitle: recipe.RecipeDescription || `${recipe.recipe_type?.RecipeType || ''} • ${recipe.RecipeTotalTime || ''}`.trim(),
            data: recipe,
          });
        }
      });
    }

    // Search coupons
    if (coupons) {
      coupons.forEach((coupon) => {
        const searchable = [
          coupon.title,
          coupon.subtitle,
          coupon.description,
          coupon.brand,
          coupon.category,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (searchable.includes(trimmed)) {
          searchResults.push({
            type: 'coupon',
            title: coupon.title,
            subtitle: coupon.subtitle || coupon.description || coupon.brand || '',
            data: coupon,
          });
        }
      });
    }

    // Limit results per category for cleaner UI
    const locationResults = searchResults.filter((r) => r.type === 'location').slice(0, 3);
    const adResults = searchResults.filter((r) => r.type === 'ad').slice(0, 3);
    const recipeResults = searchResults.filter((r) => r.type === 'recipe').slice(0, 3);
    const couponResults = searchResults.filter((r) => r.type === 'coupon').slice(0, 3);

    return [...locationResults, ...adResults, ...recipeResults, ...couponResults];
  }, [query, locations, adsWithLocations, recipes, coupons]);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  return {
    query,
    setQuery,
    results,
    clearSearch,
    hasResults: results.length > 0,
  };
}